import { useEffect, useState } from 'react';

import { LeftOutlined, RightOutlined, UserAddOutlined } from '@ant-design/icons';
import { cx } from '@emotion/css';
import { Col, Popover, Row, Space } from 'antd';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { doAddNewAccountType, doSwitchAccount, doClearSwitchAccount } from 'src/actions';
import { NavigatePathConstant } from 'src/constants';
import {
  useAccountSelector,
  useAuthenticatedUserSelector,
  useCanCreateAccount,
  useCanSwitchAccount,
  useFindOtherAccountList,
} from 'src/hooks';
import { Account, AccountStatus } from 'src/models';
import { BaseStyle, Color, Display, Image, Spacing } from 'src/styles';

import { MButton } from '../MButton/MButton';
import { MDivider } from '../MDivider/MDivider';
import { MTypography } from '../MTypography/MTypography';

import { AccountHolderFullName } from './AccountHolderFullName';
import { AccountMenuItem } from './AccountMenuItem';
import { AccountStatusBanner } from './AccountStatusBanner';
import { AccountValue } from './AccountValue';
import { getAccountSettingMenuItemList, getUserSettingMenuItemList } from './constants';
import './index.css';
import * as Styles from './Header.styles';
import { LogoutMenuItem } from './LogoutMenuItem';
import { MenuItem } from './MenuItem';
import ResourceLinks from './ResourceLinks';
import { UntilYourEntityAccountModal } from './UntilYourEntityAccountModal';
import { UserInfo } from './UserInfo';

export type DesktopProps = {
  authToken?: string;
  pathName: string;
  status?: AccountStatus;
};

export const DesktopHeader = ({ authToken, pathName, status }: DesktopProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSwitchLoading: boolean = useSelector((state: any) => state.accounts.switch.isLoading);
  const isSwitchSuccess: boolean = useSelector((state: any) => Boolean(state.accounts.switch.__succeeded));

  const [switchToAccountId, setSwitchToAccountId] = useState<string | null>(null);

  const [visible, setIsVisible] = useState(false);
  const [step, setStep] = useState<'account' | 'switch'>('account');
  const [untilYourAccountModalIsOpen, setIsUntilYourAccountModalIsOpen] = useState(false);

  const { isUserLoading } = useAuthenticatedUserSelector();
  const { account } = useAccountSelector();
  const canCreateAccount = useCanCreateAccount();
  const canSwitchAccount = useCanSwitchAccount();
  const otherAccountList = useFindOtherAccountList();

  const switchTo = (account: Account) => {
    setSwitchToAccountId(account.id);
    dispatch(doSwitchAccount({ params: { id: account.id } }));
  };

  const handleVisibleChange = (visible: boolean) => {
    setIsVisible(visible);
  };

  const onHide = () => {
    setIsVisible(false);
  };

  const onGoToSwitchStep = () => {
    setStep('switch');
  };

  const onGoBackToAccountStep = () => {
    setStep('account');
  };

  const onCreateAccount = () => {
    if (account?.type.isEntity && !account.isEntityAccountTypeActive && account.isRoot) {
      setIsVisible(false);
      setIsUntilYourAccountModalIsOpen(true);

      return;
    }
    dispatch(doAddNewAccountType());
    setIsVisible(false);
    navigate('/accounts/personal-information');
  };

  const onCurrentAccount = () => {
    setIsVisible(false);
  };

  const renderOtherAccountList = () => {
    if (isEmpty(otherAccountList)) return null;

    return (
      <>
        <Row>
          <MDivider />
        </Row>
        <Row data-testid={'dropdown-menu-other-accounts-row'}>
          <Col span={24}>
            <MTypography
              variant='body1'
              color={Color.Gray7}
              className={Styles.menuSubtitle}
              testId='dropdown-menu-other-accounts-text'>
              OTHER ACCOUNTS
            </MTypography>
          </Col>
          {otherAccountList.map(acc => (
            <Col span={24} key={acc.id}>
              <AccountMenuItem
                account={acc}
                onClick={() => switchTo(acc)}
                isLoading={switchToAccountId === acc.id && (isSwitchLoading || isUserLoading)}
              />
            </Col>
          ))}
        </Row>
      </>
    );
  };

  const renderAccountStep = () => {
    return (
      <>
        <Row justify='center' align='middle' className={Spacing.mb12}>
          <Col span={24} className={cx(Spacing.mb4, BaseStyle.level)}>
            <AccountHolderFullName account={account} alignment='center' />
          </Col>
          <Col span={24} className={Spacing.my4}>
            {account?.accountId && <AccountValue />}
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <MenuItem
              name='My Account'
              path={
                account?.isEntityAccountTypeActive ? '/accounts/entity-information' : '/accounts/personal-information'
              }
              onClick={onHide}
            />
          </Col>
        </Row>
        {canSwitchAccount && (
          <Row>
            <Col span={24}>
              <MenuItem
                name='Switch Account'
                endIcon={<RightOutlined />}
                activatable={false}
                onClick={onGoToSwitchStep}
              />
            </Col>
          </Row>
        )}
        {canCreateAccount && (
          <Row>
            <Col span={24}>
              <MenuItem
                startIcon={<UserAddOutlined style={{ fontSize: 24 }} />}
                name='Add New Account Type'
                path={
                  account?.type.isEntity && !account.isEntityAccountTypeActive && account.isRoot
                    ? undefined
                    : '/accounts/personal-information'
                }
                activatable={false}
                onClick={onCreateAccount}
              />
            </Col>
          </Row>
        )}
        {account?.accountId && (
          <>
            <Row>
              <MDivider />
            </Row>
            <Row>
              <Col span={24}>
                <MTypography variant='body1' color={Color.Gray7} className={Styles.menuSubtitle}>
                  ACCOUNT SETTINGS
                </MTypography>
              </Col>
              <Col span={24}>
                {getAccountSettingMenuItemList(account).map(aMenuItem => (
                  <MenuItem
                    key={aMenuItem.key}
                    name={aMenuItem.name}
                    path={aMenuItem.path(account?.id)}
                    startIcon={aMenuItem.startIcon?.()}
                    endIcon={aMenuItem.endIcon?.(status)}
                    onClick={onHide}
                  />
                ))}
              </Col>
            </Row>
          </>
        )}
        <Row>
          <MDivider />
        </Row>
        <Row>
          <Col span={24}>
            <MTypography variant='body1' color={Color.Gray7} className={Styles.menuSubtitle}>
              USER SETTINGS
            </MTypography>
          </Col>
          <Col span={24}>
            {getUserSettingMenuItemList(account).map(aMenuItem => (
              <MenuItem key={aMenuItem.key} name={aMenuItem.name} path={aMenuItem.path(account?.id)} onClick={onHide} />
            ))}
          </Col>
        </Row>
      </>
    );
  };

  const renderSwitchStep = () => {
    return (
      <>
        <Row>
          <Col span={24}>
            <MenuItem
              startIcon={<LeftOutlined />}
              name='Accounts'
              activatable={false}
              onClick={onGoBackToAccountStep}
            />
          </Col>
        </Row>
        <Row>
          <MDivider />
        </Row>
        <Row data-testid={'dropdown-menu-current-account-row'}>
          <Col span={24}>
            <MTypography
              variant='body1'
              color={Color.Gray7}
              className={Styles.menuSubtitle}
              testId='dropdown-menu-current-account-text'>
              CURRENT ACCOUNT
            </MTypography>
          </Col>
          <Col span={24}>
            <AccountMenuItem account={account} onClick={onCurrentAccount} />
          </Col>
        </Row>
        {renderOtherAccountList()}
      </>
    );
  };

  useEffect(() => {
    if (isSwitchSuccess) {
      setIsVisible(false);
    }

    return () => {
      dispatch(doClearSwitchAccount());
    };
  }, [isSwitchSuccess]);

  return (
    <div>
      {authToken ? (
        <div className='resourceSection'>
          <div className='resourceWidthRestriction'>
            <div>
              <ResourceLinks className='linksSmall' isAuthenticated />
            </div>
          </div>
        </div>
      ) : null}
      <Row justify='center'>
        <Col span={24}>
          <div className='mainHeader'>
            <div className='headerWidthRestriction'>
              <Link to='/'>
                <img src={Image.Logo} alt='My IPO' className='logo' />
              </Link>
              {!authToken ? (
                <div className='signedOutLinks'>
                  <ResourceLinks className='linksMedium' />
                  <Col className={cx(Display.flex, Spacing.ml32)}>
                    <Space size={[4, 0]} direction='horizontal' wrap>
                      <MButton type='secondary' onClick={() => navigate('/register')} testId={'home-btn-signup'}>
                        Sign up
                      </MButton>
                      <MButton type='secondary' onClick={() => navigate('/login')} testId={'home-btn-login'}>
                        Log in
                      </MButton>
                    </Space>
                  </Col>
                </div>
              ) : (
                <div className='headerLinkSection'>
                  <div
                    style={{
                      display: 'inline-block',
                      margin: '0 auto',
                    }}>
                    <NavLink
                      to={`/${NavigatePathConstant.MAIN}/${NavigatePathConstant.SUMMARY}`}
                      className={
                        pathName.indexOf(`/${NavigatePathConstant.SUMMARY}`) > 0 ? 'linksLargeActive' : 'linksLarge'
                      }>
                      Summary
                    </NavLink>

                    <NavLink
                      to='/offers'
                      className={pathName.startsWith('/offers') ? 'linksLargeActive' : 'linksLarge'}>
                      Offers
                    </NavLink>

                    <NavLink
                      to={`/${NavigatePathConstant.MAIN}/${NavigatePathConstant.HISTORY}`}
                      className={
                        pathName.indexOf(`/${NavigatePathConstant.HISTORY}`) > 0 ? 'linksLargeActive' : 'linksLarge'
                      }>
                      History
                    </NavLink>
                  </div>
                  <Popover
                    content={
                      <div
                        style={{
                          width: 345,
                          backgroundColor: Color.Gray1,
                          borderRadius: 12,
                          paddingTop: 22,
                          boxShadow: '0 4px 16px rgba(2, 85, 163, 0.15)',
                        }}
                        data-testid={'summary-dropdown-account-menu'}>
                        {step === 'account' ? renderAccountStep() : renderSwitchStep()}
                        <Row>
                          <MDivider />
                        </Row>
                        <Row className={cx(Spacing.pl0, Spacing.pb12)}>
                          <Col span={24}>
                            <LogoutMenuItem onClick={onHide} />
                          </Col>
                        </Row>
                      </div>
                    }
                    trigger='click'
                    open={visible}
                    onOpenChange={handleVisibleChange}
                    placement='bottomRight'>
                    <UserInfo
                      onClick={() => {
                        setStep('account');
                        handleVisibleChange(!visible);
                      }}
                    />
                  </Popover>
                </div>
              )}
            </div>
          </div>
        </Col>
        <AccountStatusBanner />
      </Row>
      <UntilYourEntityAccountModal
        open={untilYourAccountModalIsOpen}
        onClose={() => setIsUntilYourAccountModalIsOpen(false)}
      />
    </div>
  );
};
